/**
 * UpgradeRequiredDialog
 *
 * A global, action-oriented modal shown whenever a Free-plan user hits the
 * 50-task cap (HTTP 402) or any other plan-gated limit. Mounted once at the
 * app root and listens for the custom `flowtasks:upgrade-required` event so
 * any code path (TaskModal, RapidCapturePage, TemplatesPage, …) can surface
 * the upgrade prompt consistently without prop drilling.
 *
 * Event payload (all fields optional):
 *   {
 *     title:    string,  // "Task limit reached"
 *     message:  string,  // backend detail / friendly reason
 *     used:     number,  // current usage (e.g. 50)
 *     limit:    number,  // hard cap   (e.g. 50)
 *     unit:     string,  // "tasks" | "family invites" | "MB" — defaults to "tasks"
 *   }
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, Crown, X } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import { Button } from "./ui/button";

export const UPGRADE_EVENT = "flowtasks:upgrade-required";

/**
 * Convenience helper — dispatch the event from anywhere.
 *     emitUpgradeRequired({ message: detail, used: 50, limit: 50 })
 */
export function emitUpgradeRequired(detail = {}) {
  window.dispatchEvent(new CustomEvent(UPGRADE_EVENT, { detail }));
}

export default function UpgradeRequiredDialog() {
  const [open, setOpen] = useState(false);
  const [info, setInfo] = useState({});
  const navigate = useNavigate();

  useEffect(() => {
    const handler = (e) => {
      setInfo(e.detail || {});
      setOpen(true);
    };
    window.addEventListener(UPGRADE_EVENT, handler);
    return () => window.removeEventListener(UPGRADE_EVENT, handler);
  }, []);

  const {
    title = "You've hit your Free plan limit",
    message = "Free accounts are capped at 50 tasks. Upgrade to Pro for unlimited tasks, or delete a few existing ones to make room.",
    used,
    limit,
    unit = "tasks",
  } = info;

  const showCounter =
    typeof used === "number" && typeof limit === "number" && limit > 0;
  const pct = showCounter ? Math.min(100, Math.round((used / limit) * 100)) : 0;

  const onUpgrade = () => {
    setOpen(false);
    navigate("/pricing");
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent
        data-testid="upgrade-required-dialog"
        className="max-w-md p-0 overflow-hidden"
      >
        {/* Accent header */}
        <div
          className="px-6 pt-5 pb-4 border-b border-border"
          style={{
            background:
              "linear-gradient(135deg, hsla(38, 92%, 50%, 0.15) 0%, transparent 70%)",
            borderBottomColor: "hsla(38, 92%, 50%, 0.35)",
          }}
        >
          <div className="flex items-start gap-3">
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
              style={{
                backgroundColor: "hsla(38, 92%, 50%, 0.2)",
                color: "hsl(38, 92%, 50%)",
              }}
            >
              <AlertTriangle size={20} strokeWidth={2} />
            </div>
            <div className="flex-1 min-w-0">
              <DialogHeader className="text-left space-y-1">
                <DialogTitle
                  className="font-display text-lg font-black tracking-tight"
                  data-testid="upgrade-dialog-title"
                >
                  {title}
                </DialogTitle>
                <DialogDescription className="text-sm leading-relaxed text-muted-foreground">
                  {message}
                </DialogDescription>
              </DialogHeader>
            </div>
          </div>
        </div>

        {/* Counter — only when backend gave us numbers */}
        {showCounter && (
          <div className="px-6 pt-4" data-testid="upgrade-dialog-usage">
            <div className="flex items-center justify-between text-xs text-muted-foreground mb-1.5">
              <span className="font-mono uppercase tracking-wider">
                {unit} used
              </span>
              <span className="font-mono font-bold text-foreground">
                {used} / {limit}
              </span>
            </div>
            <div className="h-1.5 rounded-full bg-secondary overflow-hidden">
              <div
                className="h-full rounded-full transition-all duration-500"
                style={{
                  width: `${pct}%`,
                  backgroundColor: "hsl(38, 92%, 50%)",
                }}
              />
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="px-6 py-5 flex flex-col-reverse sm:flex-row sm:items-center sm:justify-end gap-2">
          <Button
            variant="outline"
            onClick={() => setOpen(false)}
            data-testid="upgrade-dialog-dismiss-btn"
          >
            <X size={14} className="mr-1.5" /> I'll free up space
          </Button>
          <Button
            onClick={onUpgrade}
            data-testid="upgrade-dialog-choose-plan-btn"
            className="bg-amber-500 hover:bg-amber-500/90 text-black font-bold"
          >
            <Crown size={14} className="mr-1.5" /> Choose a plan
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
